import { inject, Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { PopUpDialogInformalService } from "./confirms/popUpDialogInformal.service";
import { PopUpInformalType } from "../components/ui-components/pop-ups/pop-up-informal/pop-up-informal.model";
import { SobitService } from "./sobit.service";
import { IPayment } from "./model/get-trip-data.model";
import { PaymentStatusEnum } from "../enums/payment-status.enum";
import { LoginService } from "./login.service";
import { MatDialog } from "@angular/material/dialog";
import { catchError, defer, finalize, lastValueFrom, of } from "rxjs";
import { PendingPaymentCheckDialogComponent } from "../components/pending-payment-check-dialog/pending-payment-check-dialog.component";
import { PaymentStatusCheckOutcome } from "./model/sobit.model";
import { TenderType } from "./model/tender-type.enum";

@Injectable({
  providedIn: "root",
})
export class PaymentStatusCheckerService {
  private readonly _popUpDialogInformalService = inject(PopUpDialogInformalService);
  private readonly translate = inject(TranslateService);
  private readonly _sobitService = inject(SobitService);
  private readonly _loginService = inject(LoginService);
  private readonly _dialog = inject(MatDialog);

  /**
   * Checks and updates payment statuses for pending payments.
   * Filters payments to only include pending ones and checks their status with Sobit service.
   * 
   * @param payments Array of payments to check
   * @returns true if check was successful or no pending payments, false if check failed
   */
  async checkAndUpdatePaymentStatusesIfPending(payments: Array<IPayment>): Promise<boolean> {
    const pendingPayments = payments.filter(
      p => p.Status === PaymentStatusEnum.Pending
        && (p.PaymentType === TenderType.Card || p.PaymentType === TenderType.ElectronicVoucher),
    );
    const jwtBusinessUnit = this._loginService.user?.SelectedBusinessUnit ?? null;
    const allowedPayments = pendingPayments.filter(
      p => jwtBusinessUnit == null || p.BusinessUnitInternalNumber == null || p.BusinessUnitInternalNumber === jwtBusinessUnit,
    );
    const skippedPayments = pendingPayments.filter(
      p => jwtBusinessUnit != null && p.BusinessUnitInternalNumber != null && p.BusinessUnitInternalNumber !== jwtBusinessUnit,
    );

    if (pendingPayments.length === 0) {
      return true;
    }

    if (skippedPayments.length > 0) {
      const skippedPaymentsByBusinessUnit = new Map<number, number>();

      for (const skippedPayment of skippedPayments) {
        const businessUnit = skippedPayment.BusinessUnitInternalNumber!;
        skippedPaymentsByBusinessUnit.set(businessUnit, (skippedPaymentsByBusinessUnit.get(businessUnit) ?? 0) + 1);
      }

      const mismatchMessages = Array.from(skippedPaymentsByBusinessUnit.entries()).map(([businessUnit, count]) =>
        this.translate.instant("webPos.PaymentStatus.BusinessUnitMismatch", {
          businessUnit,
          count,
        }),
      );

      await this._popUpDialogInformalService.confirm({
        title: this.translate.instant("webPos.Info"),
        message: mismatchMessages.join("\n"),
        type: PopUpInformalType.Info,
      });
    }

    if (allowedPayments.length === 0) {
      return true;
    }

    const progressDialogRef = this._dialog.open(PendingPaymentCheckDialogComponent, {
      disableClose: true,
      panelClass: "full-screen-dialog",
    });

    const checkPaymentResult = await lastValueFrom(
      defer(() => this._sobitService.checkAndUpdatePaymentStatuses(allowedPayments)).pipe(
        catchError((error: unknown) => {
          const safeError = error instanceof Error
            ? { name: error.name, message: error.message }
            : { message: String(error) };
          console.error("[SobitPendingPayments] Verification failed; switching to offline-only mode", {
            paymentUID: null,
            errorCode: null,
            error: safeError,
          });
          return of(PaymentStatusCheckOutcome.Failed);
        }),
        finalize(() => progressDialogRef.close()),
      ),
    );

    if (checkPaymentResult === PaymentStatusCheckOutcome.Timeout) {
      await this._popUpDialogInformalService.confirm({
        title: this.translate.instant("webPos.Errors.Error"),
        message: this.translate.instant("webPos.PaymentStatus.SobitUnavailable"),
        subMessage: this.translate.instant("webPos.OnlyOfflinePaymentAvailable"),
        type: PopUpInformalType.Error,
      });
      return false;
    }

    if (checkPaymentResult === PaymentStatusCheckOutcome.Failed) {
      await this._popUpDialogInformalService.confirm({
        title: this.translate.instant("webPos.Errors.Error"),
        message: this.translate.instant("webPos.WasntAbleCheckPaymentStates"),
        subMessage: this.translate.instant("webPos.OnlyOfflinePaymentAvailable"),
        type: PopUpInformalType.Error,
      });
      return false;
    }

    return true;
  }
}
